/**
 * An `AdminSource` over `admin.*` (and the B1 procedures P1 reuses rather than duplicating).
 * Structurally typed so this module never imports server code — the same trick
 * `modules/notifications/trpc-source.ts` uses.
 */
import type {
  AccountRole,
  ActiveImpersonation,
  AdminSource,
  AuditQuery,
  AuditRow,
  BoardPermissionLevel,
  BoardPermissionsSummary,
  CapabilityCell,
  DirectoryPage,
  DirectoryQuery,
  InvitationRow,
  MyPermissionsGroup,
  PeopleSearchRow,
  PublicAccount,
  SecuritySettings,
  TeamDetail,
  TeamSummary,
  TokenOverviewRow,
  UsageStats,
  UserDetail,
  WorkspaceMemberRow,
} from './types';

interface Query<TIn, TOut> {
  query(input: TIn): Promise<TOut>;
}
interface Mutate<TIn, TOut> {
  mutate(input: TIn): Promise<TOut>;
}

export interface AdminTrpc {
  admin: {
    sharing: {
      levels: Query<undefined, BoardPermissionLevel[]>;
      board: {
        summary: Query<{ boardId: string }, BoardPermissionsSummary>;
        setPermissions: Mutate<{ boardId: string; permissions: string }, unknown>;
        myPermissions: Query<{ boardId: string }, MyPermissionsGroup[]>;
        whoLosesAccess: Query<
          { boardId: string; nextKind: string },
          { guests: string[]; members: string[]; total: number }
        >;
      };
      people: Query<{ query: string; boardId?: string }, PeopleSearchRow[]>;
    };
    users: {
      directory: Query<DirectoryQuery, DirectoryPage>;
      get: Query<{ userId: string }, UserDetail>;
      setRole: Mutate<
        { userIds: string[]; role: AccountRole },
        { updated: string[]; errors: Array<{ userId: string; code: string; message: string }> }
      >;
      deactivate: Mutate<
        { userIds: string[] },
        { updated: string[]; errors: Array<{ userId: string; code: string; message: string }> }
      >;
      reactivate: Mutate<
        { userIds: string[]; notify: boolean },
        { updated: string[]; errors: Array<{ userId: string; code: string; message: string }> }
      >;
      resetPassword: Mutate<{ userId: string }, { ok: true }>;
      unlinkPlatform: Mutate<{ userId: string }, { unlinked: boolean }>;
    };
    /** M8-P3 (T-9u) — superadmin-only "View as <user>". */
    impersonation: {
      start: Mutate<
        { userId: string },
        {
          target: { id: string; name: string; email: string; role: string; status: string };
          expiresAt: string;
        }
      >;
      active: Query<undefined, ActiveImpersonation[]>;
      end: Mutate<{ sessionId: string }, { ended: boolean }>;
    };
    invitations: {
      list: Query<
        { tab: 'sent' | 'pending' },
        { rows: InvitationRow[]; total: number; emptyReason: string | null }
      >;
      invite: Mutate<
        { paste: string; role: AccountRole; message?: string },
        {
          invited: Array<{ email: string; userId: string }>;
          errors: Array<{ email: string; code: string; message: string }>;
        }
      >;
      resend: Mutate<{ id: string }, unknown>;
      revoke: Mutate<{ id: string }, unknown>;
    };
    teams: {
      list: Query<{ includeDeleted: boolean }, TeamSummary[]>;
      get: Query<{ teamId: string }, TeamDetail>;
      create: Mutate<{ name: string; memberIds: string[] }, TeamSummary>;
      update: Mutate<{ teamId: string; name: string }, unknown>;
      remove: Mutate<{ teamId: string }, unknown>;
      addMembers: Mutate<{ teamId: string; userIds: string[] }, unknown>;
      removeMembers: Mutate<{ teamId: string; userIds: string[] }, unknown>;
      setOwner: Mutate<{ teamId: string; userId: string; isOwner: boolean }, unknown>;
    };
    settings: {
      public: Query<undefined, PublicAccount>;
      update: Mutate<Record<string, unknown>, PublicAccount>;
      security: Query<undefined, SecuritySettings>;
      updateSecurity: Mutate<Record<string, unknown>, SecuritySettings>;
      capabilities: Query<undefined, { cells: CapabilityCell[] }>;
      setCapability: Mutate<
        { action: string; role: string; allowed: boolean | null },
        { cells: CapabilityCell[] }
      >;
      logoutEveryone: Mutate<undefined, { revoked: number; people: number }>;
    };
    usage: Query<{ days: number }, UsageStats>;
    audit: {
      list: Query<AuditQuery, { rows: AuditRow[]; nextCursor: string | null }>;
      exportCsv: Query<AuditQuery, { csv: string }>;
    };
    tokens: {
      list: Query<Record<string, never>, { rows: TokenOverviewRow[] }>;
      revoke: Mutate<{ id: string }, { ok: true }>;
    };
  };
  /** P1 adds UI, not a second write path: subscribers and members stay B1's (P1 §5.1). */
  boards: {
    subscribers: {
      add: Mutate<
        { id: string; userIds?: string[]; teamIds?: string[]; role: 'owner' | 'subscriber' },
        unknown
      >;
      remove: Mutate<{ id: string; userIds?: string[]; teamIds?: string[] }, unknown>;
      setRole: Mutate<
        { id: string; userIds?: string[]; teamIds?: string[]; role: 'owner' | 'subscriber' },
        unknown
      >;
    };
  };
  workspaces: {
    members: Query<{ id: string }, WorkspaceMemberRow[]>;
    addMembers: Mutate<{ id: string; userIds: string[]; role: 'owner' | 'member' }, unknown>;
    removeMembers: Mutate<{ id: string; userIds: string[] }, unknown>;
  };
}

export function createAdminSource(trpc: AdminTrpc): AdminSource {
  const admin = trpc.admin;
  return {
    directory: (query) => admin.users.directory.query(query),
    user: (userId) => admin.users.get.query({ userId }),
    setRole: (input) => admin.users.setRole.mutate(input),
    deactivate: (userIds) => admin.users.deactivate.mutate({ userIds }),
    reactivate: (input) => admin.users.reactivate.mutate(input),
    resetPassword: (userId) => admin.users.resetPassword.mutate({ userId }),
    unlinkPlatform: (userId) => admin.users.unlinkPlatform.mutate({ userId }),

    startImpersonation: (userId) => admin.impersonation.start.mutate({ userId }),
    activeImpersonations: () => admin.impersonation.active.query(undefined),
    endImpersonation: (sessionId) => admin.impersonation.end.mutate({ sessionId }),

    invitations: (tab) => admin.invitations.list.query({ tab }),
    invite: (input) => admin.invitations.invite.mutate(input),
    resendInvite: (id) => admin.invitations.resend.mutate({ id }),
    revokeInvite: (id) => admin.invitations.revoke.mutate({ id }),

    teams: () => admin.teams.list.query({ includeDeleted: false }),
    team: (teamId) => admin.teams.get.query({ teamId }),
    createTeam: (input) => admin.teams.create.mutate(input),
    updateTeam: (input) => admin.teams.update.mutate(input),
    deleteTeam: (teamId) => admin.teams.remove.mutate({ teamId }),
    addTeamMembers: (input) => admin.teams.addMembers.mutate(input),
    removeTeamMembers: (input) => admin.teams.removeMembers.mutate(input),
    setTeamOwner: (input) => admin.teams.setOwner.mutate(input),

    account: () => admin.settings.public.query(undefined),
    updateAccount: (patch) => admin.settings.update.mutate(patch),
    security: () => admin.settings.security.query(undefined),
    updateSecurity: (patch) => admin.settings.updateSecurity.mutate(patch),
    capabilities: () => admin.settings.capabilities.query(undefined),
    setCapability: (input) => admin.settings.setCapability.mutate(input),
    logoutEveryone: () => admin.settings.logoutEveryone.mutate(undefined),

    usage: (days) => admin.usage.query({ days }),
    audit: (input) => admin.audit.list.query(input),
    exportAudit: (input) => admin.audit.exportCsv.query(input),
    tokens: () => admin.tokens.list.query({}),
    revokeToken: (id) => admin.tokens.revoke.mutate({ id }),

    levels: () => admin.sharing.levels.query(undefined),
    boardSummary: (boardId) => admin.sharing.board.summary.query({ boardId }),
    setBoardPermissions: (input) => admin.sharing.board.setPermissions.mutate(input),
    myBoardPermissions: (boardId) => admin.sharing.board.myPermissions.query({ boardId }),
    whoLosesAccess: (input) => admin.sharing.board.whoLosesAccess.query(input),
    people: (input) => admin.sharing.people.query(input),

    addBoardPrincipals: ({ boardId, userIds, teamIds, role }) =>
      trpc.boards.subscribers.add.mutate({
        id: boardId,
        ...(userIds ? { userIds } : {}),
        ...(teamIds ? { teamIds } : {}),
        role,
      }),
    removeBoardPrincipals: ({ boardId, userIds, teamIds }) =>
      trpc.boards.subscribers.remove.mutate({
        id: boardId,
        ...(userIds ? { userIds } : {}),
        ...(teamIds ? { teamIds } : {}),
      }),
    setBoardPrincipalRole: ({ boardId, userIds, teamIds, role }) =>
      trpc.boards.subscribers.setRole.mutate({
        id: boardId,
        ...(userIds ? { userIds } : {}),
        ...(teamIds ? { teamIds } : {}),
        role,
      }),

    workspaceMembers: (workspaceId) => trpc.workspaces.members.query({ id: workspaceId }),
    addWorkspaceMembers: (input) => trpc.workspaces.addMembers.mutate(input),
    removeWorkspaceMembers: (input) => trpc.workspaces.removeMembers.mutate(input),
  };
}
